/**
 * micro:bit 놀이터 1단계 — 보드 화면 · 입력 읽기(PICNIC-MB-FIELD-001 v0.2 §2 통로 ① 화면 읽기 · ② 입력 따라하기 · SR-RUN-07).
 *
 * 아이의 프로그램은 Bench 의 MakeCode 시뮬레이터(또는 실제 보드)에서 돈다. 프로젝트에 실린 놀이터 연결 확장(picnic-bench
 * `contracts/makecode/picnic-field.ts`)이 0.1초마다 보드 모습을 한 줄로 알리고, Bench 편집기 탭이 그 줄을 이 탭으로 넘긴다:
 *   `{type:"bench.mbLive", v:1, projectId, frame:"PF1 L=<25칸 0~9> A=<0|1> B=<0|1> G=<0|1> S=<0|1> X=<mg> Y=<mg>"}`
 * 받는 것은 **설정된 Bench 웹 출처에서만**(event.origin 정확히 같음 — 'Bench 에서 고치기' 와 같은 규칙). 그 밖은 버린다.
 */
import type { Dpad, RemoteInput } from "@picnic-field/local-host/protocol";
import { ICONS } from "./icons";

export const MB_LIVE_TYPE = "bench.mbLive";
export const MB_LIVE_V = 1;

/** 보드 한 순간 — LED 25칸(가로 줄 순서 · 밝기 0~9) · 버튼 · 로고 · 흔들기 · 기울기(mg) */
export interface MbFrame {
  readonly leds: readonly number[];
  readonly a: boolean;
  readonly b: boolean;
  readonly logo: boolean;
  readonly shake: boolean;
  readonly ax: number;
  readonly ay: number;
  /** 센서(5단계 센서 월드 — 놀이터가 요청했을 때만 줄 끝에 붙는다 · 없으면 undefined) */
  readonly sensors?: Sensors;
  /** 연주한 음(§6.10 — 보드가 음을 하나라도 냈으면 ` TN=<센 수> TF=<마지막 음 Hz>`) — n 이 바뀌면 새 음 */
  readonly tone?: { readonly n: number; readonly hz: number };
}

/** 빛(0~255) · 온도(°C) · 소리(0~255) · 나침반(0~359°) */
export interface Sensors {
  readonly light?: number;
  readonly temp?: number;
  readonly sound?: number;
  readonly heading?: number;
}
const SENSOR_KEY: Readonly<Record<string, keyof Sensors>> = { LL: "light", TC: "temp", SL: "sound", CH: "heading" };

export interface MbLive {
  readonly projectId: string | null;
  readonly frame: MbFrame;
}

const LINE = /^PF1 L=([0-9]{25}) A=([01]) B=([01]) G=([01]) S=([01]) X=(-?\d{1,5}) Y=(-?\d{1,5})((?: [A-Z]{2}=-?\d{1,5}){0,8})$/;
const PROJECT = /^[A-Za-z0-9_.:-]{1,100}$/;

/** 한 줄 → 보드 모습. 모양이 다르면 null(다른 판 · 잘린 줄) */
export function parseFrame(line: string): MbFrame | null {
  const m = LINE.exec(line.trim());
  if (!m) return null;
  return {
    leds: m[1]!.split("").map(Number),
    a: m[2] === "1",
    b: m[3] === "1",
    logo: m[4] === "1",
    shake: m[5] === "1",
    ax: clampMg(Number(m[6])),
    ay: clampMg(Number(m[7])),
    ...(m[8] ? { sensors: sensorsOf(m[8]) } : {}),
    ...(m[8] && toneOf(m[8]) ? { tone: toneOf(m[8])! } : {}),
  };
}

/** postMessage 거르기 — 출처 · 종류 · 판 · 줄 모양이 맞을 때만 */
export function acceptLive(
  ev: Pick<MessageEvent, "origin" | "data">,
  benchOrigin: string | null,
): MbLive | null {
  if (!benchOrigin || ev.origin !== benchOrigin) return null;
  const d = ev.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object" || d.type !== MB_LIVE_TYPE || d.v !== MB_LIVE_V) return null;
  if (typeof d.frame !== "string" || d.frame.length > 200) return null;
  const frame = parseFrame(d.frame);
  if (!frame) return null;
  return { projectId: typeof d.projectId === "string" && PROJECT.test(d.projectId) ? d.projectId : null, frame };
}

/** 기울기 문턱(mg) — 이만큼 기울여야 방향이 된다(손 떨림은 무시) */
export const TILT_MG = 300;

/**
 * 보드 → 놀이터 리모컨 입력(FIELD-CRQ-001 RemoteInput). 기울기 = 방향(앞으로 기울이면 Y 가 음수 → 북쪽 · 오른쪽 = X 양수 → 동쪽 ·
 * 둘 다면 대각), A · B 버튼 = A · B. 슬라이더는 쓰지 않는다(0). 흔들기 · 로고는 놀이터가 따로 읽는다(점프 · 응원).
 */
export function toRemote(f: MbFrame | null): RemoteInput {
  if (!f) return { dpad: null, a: false, b: false, slider: 0 };
  const ns = f.ay <= -TILT_MG ? "n" : f.ay >= TILT_MG ? "s" : "";
  const ew = f.ax >= TILT_MG ? "e" : f.ax <= -TILT_MG ? "w" : "";
  const dpad = (ns + ew || null) as Dpad;
  return { dpad, a: f.a, b: f.b, slider: 0 };
}

/** 지금 화면이 어떤 아이콘인가 — 밝기 5 이상을 켜짐으로 보고 40개 표와 똑같으면 그 이름. 아니면 null(숫자 · 글자 · 내 그림) */
export function iconOf(leds: readonly number[]): string | null {
  if (leds.length !== 25) return null;
  const bits = leds.map((v) => (v >= 5 ? "1" : "0")).join("");
  for (const [name, b] of Object.entries(ICONS)) if (b === bits) return name;
  return null;
}

/** 화면이 꺼져 있는가(아직 아무것도 안 보임) */
export const isBlank = (leds: readonly number[]): boolean => leds.every((v) => v === 0);

function clampMg(v: number): number {
  return Number.isFinite(v) ? Math.max(-2048, Math.min(2048, Math.trunc(v))) : 0;
}

/** 줄 끝 센서 칸(" LL=12 TC=24") → 값(아는 이름만 · 범위 밖은 버림) */
function toneOf(tail: string): { n: number; hz: number } | null {
  const n = /(?:^| )TN=(\d{1,5})/.exec(tail);
  const hz = /(?:^| )TF=(\d{1,5})/.exec(tail);
  return n && hz ? { n: Number(n[1]), hz: Number(hz[1]) } : null;
}

function sensorsOf(tail: string): Sensors {
  const out: Record<string, number> = {};
  for (const part of tail.trim().split(" ")) {
    const [k, v] = part.split("=");
    const key = SENSOR_KEY[k ?? ""];
    const n = Number(v);
    if (key && Number.isFinite(n) && n >= -50 && n <= 1000) out[key] = n;
  }
  return out as Sensors;
}

